/**
 * Session Persistence Hook
 * Manages localStorage persistence for Athena conversations and sessions
 */

import { useState, useCallback, useEffect } from 'react';
import { STORAGE_KEYS, LIMITS } from '../../../config/athenaWorkflowConfig';
import { deleteSession } from '../../../api/sessions';

/**
 * Generate unique conversation ID
 */
const generateConversationId = () => {
  return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generate unique session ID
 */
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const useSessionPersistence = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load conversations from localStorage
   */
  const loadConversations = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CONVERSATIONS);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Filter out expired conversations
        const now = Date.now();
        const maxAge = LIMITS.MAX_CONVERSATION_AGE_DAYS * 24 * 60 * 60 * 1000;
        const valid = parsed.filter(conv => {
          const age = now - new Date(conv.createdAt || conv.lastModified).getTime();
          return age < maxAge;
        });

        // Limit to max stored conversations
        const limited = valid.slice(0, LIMITS.MAX_STORED_CONVERSATIONS);
        return limited;
      }
      return [];
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setError('Failed to load saved conversations');
      return [];
    }
  }, []);

  /**
   * Save conversations to localStorage
   */
  const saveConversations = useCallback((convs) => {
    try {
      // Limit to max stored conversations
      const limited = convs.slice(0, LIMITS.MAX_STORED_CONVERSATIONS);
      localStorage.setItem(STORAGE_KEYS.CONVERSATIONS, JSON.stringify(limited));
      setError(null);
      return true;
    } catch (error) {
      console.error('Failed to save conversations:', error);

      // Check if it's a storage quota error
      if (error.name === 'QuotaExceededError') {
        // Try to clean up old conversations and retry
        try {
          const cleaned = convs
            .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))
            .slice(0, Math.floor(LIMITS.MAX_STORED_CONVERSATIONS / 2));
          localStorage.setItem(STORAGE_KEYS.CONVERSATIONS, JSON.stringify(cleaned));
          setConversations(cleaned);
          setError('Storage full - removed old conversations');
          return true;
        } catch (retryError) {
          setError('Storage full - unable to save');
          return false;
        }
      }

      setError('Failed to save conversations');
      return false;
    }
  }, []);

  /**
   * Load active conversation ID from localStorage
   */
  const loadActiveConversationId = useCallback(() => {
    try {
      return localStorage.getItem(STORAGE_KEYS.ACTIVE_CONVERSATION_ID);
    } catch (error) {
      console.error('Failed to load active conversation ID:', error);
      return null;
    }
  }, []);

  /**
   * Save active conversation ID to localStorage
   */
  const saveActiveConversationId = useCallback((id) => {
    try {
      if (id === null) {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_CONVERSATION_ID);
      } else {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_CONVERSATION_ID, id);
      }
      setError(null);
    } catch (error) {
      console.error('Failed to save active conversation ID:', error);
      setError('Failed to save active conversation');
    }
  }, []);

  /**
   * Create a new conversation
   */
  const createConversation = useCallback((initialData = {}) => {
    // Handle backendSessionId and backendTenantId if provided
    const backendSession = initialData.backendSessionId ? {
      sessionId: initialData.backendSessionId,
      tenantId: initialData.backendTenantId,
      threadId: initialData.backendThreadId || null,
      fileId: null,
      hasActiveSession: false,
      lastSyncedAt: new Date().toISOString()
    } : null;

    const newConversation = {
      id: generateConversationId(),
      title: `New Analysis ${conversations.length + 1}`,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      messages: [],
      taraSession: {
        step: 0,
        sessionId: generateSessionId(),
        itemDefinition: {},
        documents: [],
        assets: [],
        damageScenarios: [],
        threats: [],
        attackPaths: [],
        feasibilityAssessments: [],
        risks: [],
        treatments: {},
        cybersecurityRequirements: [],
        report: null,
        analysisComplete: false,
        completedSteps: [],
        isProcessing: false
      },
      backendSession: backendSession,
      expertValidations: {},
      ...initialData
    };

    // Remove temporary properties used for construction
    delete newConversation.backendSessionId;
    delete newConversation.backendTenantId;
    delete newConversation.backendThreadId;

    console.log('📝 Creating conversation with backend session:', {
      conversationId: newConversation.id,
      backendSession: newConversation.backendSession
    });

    // Use functional setState to ensure we have the latest state
    // This is critical when creating multiple conversations in quick succession
    setConversations(prevConversations => {
      const updated = [newConversation, ...prevConversations];
      saveConversations(updated);
      return updated;
    });

    setActiveConversationId(newConversation.id);
    saveActiveConversationId(newConversation.id);

    return newConversation;
  }, [saveConversations, saveActiveConversationId]);

  /**
   * Update a conversation
   */
  const updateConversation = useCallback((id, updates, updateTimestamp = true) => {
    const updated = conversations.map(conv => {
      if (conv.id === id) {
        const updatedConv = {
          ...conv,
          ...updates
        };

        if (updateTimestamp) {
          updatedConv.lastModified = new Date().toISOString();
        }

        return updatedConv;
      }
      return conv;
    });

    setConversations(updated);
    saveConversations(updated);
    return updated.find(conv => conv.id === id);
  }, [conversations, saveConversations]);

  /**
   * Update the active conversation
   */
  const updateActiveConversation = useCallback((updates, updateTimestamp = true) => {
    if (!activeConversationId) return null;
    return updateConversation(activeConversationId, updates, updateTimestamp);
  }, [activeConversationId, updateConversation]);

  /**
   * Delete a conversation
   */
  const deleteConversation = useCallback(async (id) => {
    console.log(`🗑️ Starting deletion of conversation ${id}`);
    
    // Find the conversation to get backend session info
    const conversationToDelete = conversations.find(conv => conv.id === id);
    
    if (!conversationToDelete) {
      console.error(`❌ Conversation ${id} not found`);
      return false;
    }
    
    console.log(`📋 Found conversation: ${conversationToDelete.title || 'Untitled'}`);
    console.log(`📋 Backend session info:`, {
      sessionId: conversationToDelete.backendSession?.sessionId || 'None',
      threadId: conversationToDelete.backendSession?.threadId || 'None',
      conversationId: id
    });
    
    // Determine session ID to delete
    // Priority: backendSession.sessionId, then fall back to conversation.id (which is the session_id)
    const sessionIdToDelete = conversationToDelete.backendSession?.sessionId || id;
    
    if (sessionIdToDelete) {
      console.log(`🗑️ Deleting backend session: ${sessionIdToDelete}`);
      
      try {
        // Call backend to delete the session
        const result = await deleteSession(sessionIdToDelete);
        console.log(`✅ Successfully deleted backend session: ${sessionIdToDelete}`, result);
      } catch (deleteError) {
        console.error(`❌ Error deleting backend session ${sessionIdToDelete}:`, deleteError);
        // Continue with frontend deletion even if backend deletion fails
      }
    } else {
      console.log(`⚠️ No session_id found for conversation ${id} - UI-only deletion`);
    }
    
    // Delete from frontend
    const updated = conversations.filter(conv => conv.id !== id);
    setConversations(updated);
    saveConversations(updated);

    // If deleting active conversation, switch to most recent
    if (id === activeConversationId) {
      const mostRecent = updated.sort((a, b) =>
        new Date(b.lastModified) - new Date(a.lastModified)
      )[0];

      if (mostRecent) {
        setActiveConversationId(mostRecent.id);
        saveActiveConversationId(mostRecent.id);
      } else {
        setActiveConversationId(null);
        saveActiveConversationId(null);
      }
    }
    
    console.log(`✅ Conversation ${id} deleted successfully`);
    return true;
  }, [conversations, activeConversationId, saveConversations, saveActiveConversationId]);

  /**
   * Clear all conversations
   */
  const clearAllConversations = useCallback(() => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      setConversations([]);
      setActiveConversationId(null);
      setError(null);
      return true;
    } catch (error) {
      console.error('Failed to clear conversations:', error);
      setError('Failed to clear data');
      return false;
    }
  }, []);

  /**
   * Get the active conversation
   */
  const getActiveConversation = useCallback(() => {
    return conversations.find(conv => conv.id === activeConversationId);
  }, [conversations, activeConversationId]);

  /**
   * Switch to a different conversation
   */
  const switchConversation = useCallback((id) => {
    const conversation = conversations.find(conv => conv.id === id);
    if (conversation) {
      setActiveConversationId(id);
      saveActiveConversationId(id);
      return conversation;
    }
    return null;
  }, [conversations, saveActiveConversationId]);

  /**
   * Update backend session info for active conversation
   */
  const updateBackendSession = useCallback((sessionInfo) => {
    if (!activeConversationId) return null;

    const updates = {
      backendSession: {
        ...getActiveConversation()?.backendSession,
        ...sessionInfo,
        lastSyncedAt: new Date().toISOString()
      }
    };

    return updateConversation(activeConversationId, updates, false);
  }, [activeConversationId, getActiveConversation, updateConversation]);

  /**
   * Sync conversations with backend sessions
   */
  const syncWithBackend = useCallback((backendConversations) => {
    try {
      // Merge backend sessions with local conversations
      const mergedConversations = [...conversations];

      backendConversations.forEach(backendConv => {
        const existingIndex = mergedConversations.findIndex(
          conv => conv.id === backendConv.id
        );

        if (existingIndex >= 0) {
          // Update existing conversation with backend data
          mergedConversations[existingIndex] = {
            ...mergedConversations[existingIndex],
            ...backendConv,
            lastSyncedAt: new Date().toISOString()
          };
        } else {
          // Add new conversation from backend
          mergedConversations.push({
            ...backendConv,
            messages: [],
            lastSyncedAt: new Date().toISOString()
          });
        }
      });

      // Update local state and storage
      setConversations(mergedConversations);
      saveConversations(mergedConversations);

      return mergedConversations;
    } catch (error) {
      console.error('Failed to sync with backend:', error);
      setError('Failed to sync with backend sessions');
      return conversations;
    }
  }, [conversations, saveConversations]);

  /**
   * Store document info in active conversation
   */
  const storeDocumentInfo = useCallback((file, uploadResult) => {
    if (!activeConversationId || !file) return null;

    const documentInfo = {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      uploadedAt: new Date().toISOString(),
      backendFileId: uploadResult?.file_id || null,
      backendFilename: uploadResult?.filename || null,
      backendFileSize: uploadResult?.file_size || null,
      backendFileType: uploadResult?.file_type || null,
      uploadTimestamp: uploadResult?.upload_timestamp || null
    };

    // Update backend session with document info
    updateBackendSession({
      documentInfo,
      fileId: uploadResult?.file_id || null
    });

    // Update documents in taraSession
    const conversation = getActiveConversation();
    if (conversation) {
      const updates = {
        taraSession: {
          ...conversation.taraSession,
          documents: [
            ...conversation.taraSession.documents,
            {
              ...documentInfo,
              uploaded: true
            }
          ]
        }
      };
      return updateConversation(activeConversationId, updates, true);
    }

    return null;
  }, [activeConversationId, getActiveConversation, updateConversation, updateBackendSession]);

  /**
   * Initial load from localStorage
   */
  useEffect(() => {
    setIsLoading(true);

    // Load conversations
    const loadedConversations = loadConversations();
    setConversations(loadedConversations);

    // Load active conversation ID
    const loadedActiveId = loadActiveConversationId();

    // Validate that active ID exists in loaded conversations
    if (loadedActiveId && loadedConversations.find(c => c.id === loadedActiveId)) {
      setActiveConversationId(loadedActiveId);
    } else if (loadedConversations.length > 0) {
      // If no valid active ID, select most recent
      const mostRecent = loadedConversations.sort((a, b) =>
        new Date(b.lastModified) - new Date(a.lastModified)
      )[0];
      setActiveConversationId(mostRecent.id);
      saveActiveConversationId(mostRecent.id);
    }

    setIsLoading(false);
  }, []); // Run only once on mount

  return {
    // State
    conversations,
    activeConversationId,
    activeConversation: getActiveConversation(),
    isLoading,
    error,

    // Actions
    createConversation,
    updateConversation,
    updateActiveConversation,
    deleteConversation,
    clearAllConversations,
    switchConversation,
    updateBackendSession,
    syncWithBackend,
    storeDocumentInfo,

    // Utilities
    generateConversationId,
    generateSessionId
  };
};